import { SidebarProvider } from "@/components/ui/sidebar";
import { AdminSidebar } from "@/components/AdminSidebar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Code, Info } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { FileCode, ServerCog } from "lucide-react";

const BillingWebhook = () => {
  return (
    <SidebarProvider>
      <div className="flex min-h-screen w-full bg-gray-100">
        <AdminSidebar />
        <div className="flex-1">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <div className="mb-12">
              <h1 className="text-4xl font-bold" style={{ color: "#008169" }}>
                Payment API
              </h1>
              <p className="text-xl text-gray-600 mt-2">
                API endpoint for receiving and processing payment notifications
              </p>
            </div>

            <Alert className="mb-8 border-amber-300 bg-amber-50">
              <Info className="h-5 w-5 text-amber-600" />
              <AlertTitle className="text-amber-800">API Testing Mode</AlertTitle>
              <AlertDescription className="text-amber-700">
                The API is currently in testing mode. Make sure to include the correct bill component ID when making requests.
                You can find valid bill component IDs in the Bills management section.
              </AlertDescription>
            </Alert>

            <Card className="mb-8">
              <CardHeader>
                <CardTitle className="text-xl flex items-center">
                  <Code className="mr-2 h-5 w-5" />
                  API Documentation
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <h3 className="text-lg font-semibold mb-2">Endpoint</h3>
                    <div className="bg-gray-100 p-3 rounded-md font-mono text-sm">
                      POST https://swhwykzorcbiutczhjgn.supabase.co/functions/v1/update-bill-status
                    </div>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold mb-2">Headers</h3>
                    <div className="bg-gray-100 p-3 rounded-md font-mono text-sm">
                      Content-Type: application/json<br />
                      Authorization: Bearer your-payment-api-key
                    </div>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold mb-2">Request Body</h3>
                    <pre className="bg-gray-100 p-3 rounded-md font-mono text-sm overflow-auto">
{`{
  "billComponentId": "component-id-of-the-bill",  // Required: The component ID of the bill to update
  "action": "paid",                               // Required: Either "paid" or "cancel"
  "apiKey": "your-payment-api-key"                // Optional if using Authorization header
}`}
                    </pre>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold mb-2">Response (Success)</h3>
                    <pre className="bg-gray-100 p-3 rounded-md font-mono text-sm overflow-auto">
{`{
  "success": true,
  "message": "Bill with component ID xyz-123 has been marked as paid successfully"
}`}
                    </pre>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold mb-2">Response (Error)</h3>
                    <pre className="bg-gray-100 p-3 rounded-md font-mono text-sm overflow-auto">
{`{
  "success": false,
  "error": "Error message details"
}`}
                    </pre>
                  </div>

                  <div className="mt-4 bg-yellow-50 border-l-4 border-yellow-400 p-4">
                    <div className="flex">
                      <div className="ml-3">
                        <h3 className="text-sm font-medium text-yellow-800">Important Note</h3>
                        <div className="mt-2 text-sm text-yellow-700">
                          <p>
                            For security reasons, you must use the API key provided by the administrator. 
                            This key can be provided either in the request body as "apiKey" or as a Bearer token in the Authorization header.
                            Using the Authorization header is recommended for better security.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-xl">Testing the API</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="mb-4">
                  You can test the API using tools like Postman or cURL. Here's an example cURL command:
                </p>
                <pre className="bg-gray-100 p-3 rounded-md font-mono text-sm overflow-auto mb-4">
{`curl -X POST https://swhwykzorcbiutczhjgn.supabase.co/functions/v1/update-bill-status \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer your-payment-api-key" \\
  -d '{
    "billComponentId": "component-id-of-the-bill",
    "action": "paid"
  }'`}
                </pre>
                
                <p className="mb-4">
                  Or if you prefer to include the API key in the request body:
                </p>
                <pre className="bg-gray-100 p-3 rounded-md font-mono text-sm overflow-auto">
{`curl -X POST https://swhwykzorcbiutczhjgn.supabase.co/functions/v1/update-bill-status \\
  -H "Content-Type: application/json" \\
  -d '{
    "billComponentId": "component-id-of-the-bill",
    "action": "paid",
    "apiKey": "your-payment-api-key"
  }'`}
                </pre>
              </CardContent>
            </Card>

            <section className="mt-12">
              <h3 className="text-xl font-bold mb-4">Related APIs</h3>
              <div className="space-y-4">
                <div className="flex flex-col gap-2">
                  <h4 className="text-lg font-semibold">Pricing API</h4>
                  <p className="text-gray-600">
                    Get pricing information for products and plans.
                  </p>
                  <a
                    href="/pricing-api-docs"
                    className="text-blue-600 hover:underline flex items-center"
                  >
                    <FileCode className="h-4 w-4 mr-1" />
                    View Pricing API Documentation
                  </a>
                </div>
                
                <div className="flex flex-col gap-2">
                  <h4 className="text-lg font-semibold">Subscription Status API</h4>
                  <p className="text-gray-600">
                    Check if schools have active subscriptions.
                  </p>
                  <a
                    href="/admin/subscription-status-webhook"
                    className="text-blue-600 hover:underline flex items-center"
                  >
                    <ServerCog className="h-4 w-4 mr-1" />
                    View Subscription Status API Documentation
                  </a>
                </div>
              </div>
            </section>
          </div>
        </div>
      </div>
    </SidebarProvider>
  );
};

export default BillingWebhook;
